import ArrowPathIcon from '@heroicons/react/20/solid/ArrowPathIcon.js';
import React from 'react';
import { useObjectRef } from 'react-aria';
import {
  Collection as AriaCollection,
  type Key,
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  ListBoxLoadMoreItem as AriaListBoxLoadMoreItem,
  type Selection,
  Virtualizer,
} from 'react-aria-components';

import { forwardRef } from '../../generic-forward-ref';
import { tv } from '../../tv';
import { rowStyles } from '../overlay/row';
import { Spinner } from '../spinner/spinner';
import { type OptionLayout, OptionLayoutContext, OptionListLayout } from './layout';
import {
  ListSuppressionContext,
  useListSuppression,
  withoutSuppressedToggles,
} from './suppression';

/* The virtualizer's starting geometry (picker.md): a one-line option is the
 * 32px row, a heading 24px, the list inset by the popup's 4px. Rows with a
 * description measure taller once rendered, the React Spectrum Picker's
 * own arrangement, so a list never lays out more than the rows on screen.
 * The loading-more row is never measured: the layout pins it at the row height. */
const LAYOUT_OPTIONS = {
  estimatedRowHeight: 32,
  estimatedHeadingHeight: 24,
  loaderSize: 32,
  padding: 4,
};

/* Cards stand apart by the 8px gap and start at a record's height; they
 * meet the list's edges, aligned with the fields above them, which a card
 * can afford because its focus is its edge, never a lift or an outside ring.
 * The list ends 4px past the last card, the reach of its raised shadow. */
const CARD_LAYOUT_OPTIONS = {
  ...LAYOUT_OPTIONS,
  estimatedRowHeight: 80,
  gap: 8,
  padding: 0,
  endInset: 4,
};

/* The retry row's key: every list that pages owns it, so the pickers take a
 * press on it as a retry, never as a choice. */
export const RETRY_LOAD_MORE_KEY = 'ck-retry-load-more';

/* The row a failed page leaves at the list's end: the retry glyph in the
 * check column before the caller's message, an option like the Combobox's
 * create row, so a listbox can hold the action a button inside it could not. */
function RetryLoadMoreOption({ message }: { message: string }) {
  const { item, itemCheck, itemLabel } = rowStyles({ truncate: true });
  return (
    <AriaListBoxItem
      id={RETRY_LOAD_MORE_KEY}
      textValue={message}
      className={item()}
      data-component="retry-load-more"
    >
      <ArrowPathIcon
        aria-hidden="true"
        focusable="false"
        className={itemCheck({ selected: true })}
      />
      <span className={itemLabel()}>{message}</span>
    </AriaListBoxItem>
  );
}

const listStyles = tv({
  slots: {
    /* A standalone list's own box: React Aria renders a template and focus
     * sentinels beside the list, and a host spacing its children (a Chakra
     * Stack) must see one element. It scrolls inside the height its parent
     * gives it. */
    root: 'ck:flex ck:min-h-0 ck:max-h-full ck:flex-col',
    /* The list is the scroll container; the layout owns the inset, so a CSS
     * padding here would double it. */
    list: 'ck:min-h-0 ck:overflow-auto ck:outline-none',
    empty: 'ck:px-2 ck:py-item ck:font-sans ck:text-sm ck:text-text-muted',
  },
  variants: {
    /* A popup's list takes the height the popup has left under its filter. */
    standalone: {
      true: {},
      false: { list: 'ck:flex-1' },
    },
  },
  defaultVariants: { standalone: false },
});

export interface OptionItemsStaticProps {
  items?: never;
  /** Option and OptionSection elements. */
  children: React.ReactNode;
}

export interface OptionItemsDynamicProps<T> {
  /** The options' data; each item renders through `children`. Keep the array's identity stable. */
  items: Iterable<T>;
  /** Renders one item as an Option (or an OptionSection). */
  children: (item: T) => React.ReactElement;
}

/** How every picker takes its options: static elements, or data with a render function. */
export type OptionItemsProps<T> = OptionItemsStaticProps | OptionItemsDynamicProps<T>;

/** The internal list's props: the public unions, loosened so the pickers pass them straight through. */
export interface OptionListProps<T> {
  items?: Iterable<T>;
  children: React.ReactNode | ((item: T) => React.ReactNode);
  /** The list element, for the standalone ListBox's forwarded ref. */
  listRef?: React.ForwardedRef<HTMLDivElement>;
  /** Values the render function closes over, so a change re-renders the options. */
  dependencies?: unknown[];
  /** Shown in place of the options when there are none (a filter with no match). */
  emptyMessage?: string;
  /** Options are on their way: an empty list shows a spinner row rather than the message. */
  isLoading?: boolean;
  /** One more option after the caller's, outside their render function (a Combobox's create row). */
  trailing?: React.ReactNode;
  /** Called as the end of the list nears view; never while a page or the first load is on its way. */
  onLoadMore?: () => void;
  /** The next page is on its way: a spinner row after the options. */
  isLoadingMore?: boolean;
  /** The next page failed: the list ends in a row with this message that asks again (`onLoadMore`). */
  loadMoreError?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /** The standalone list's selection; inside a picker, the picker's state drives the list. */
  selectionMode?: 'single' | 'multiple';
  selectedKeys?: Selection;
  onSelectionChange?: (keys: Selection) => void;
  isStandalone?: boolean;
  layout?: OptionLayout;
}

/**
 * The options list every picker renders: React Aria's ListBox, always
 * virtualized (picker.md: a list is never slow because it is long), on the
 * row recipe Menu shares. Inside a Select or a Combobox the picker's state
 * drives it; `ListBox` is the standalone, controlled form. Internal.
 */
export function OptionList<T extends object>({
  items,
  children,
  listRef,
  dependencies,
  emptyMessage,
  isLoading = false,
  trailing,
  onLoadMore,
  isLoadingMore = false,
  loadMoreError,
  selectionMode,
  selectedKeys,
  onSelectionChange,
  isStandalone = false,
  layout = 'rows',
  ...labelling
}: OptionListProps<T>) {
  const { root, list, empty } = listStyles({ standalone: isStandalone });
  const emptyState = isLoading ? (
    <div className={empty()}>
      <Spinner size="sm" />
    </div>
  ) : emptyMessage ? (
    <div className={empty()}>{emptyMessage}</div>
  ) : null;
  const options = (
    <OptionLayoutContext.Provider value={layout}>
      <Virtualizer
        layout={OptionListLayout}
        layoutOptions={layout === 'cards' ? CARD_LAYOUT_OPTIONS : LAYOUT_OPTIONS}
      >
        <AriaListBox
          {...labelling}
          ref={listRef}
          selectionMode={selectionMode}
          selectedKeys={selectedKeys}
          onSelectionChange={onSelectionChange}
          /* A single choice never empties by re-pressing it (the Select and
           * MenuSection rule); a picker's own state already enforces this. */
          disallowEmptySelection={isStandalone && selectionMode === 'single'}
          escapeKeyBehavior="none"
          className={list()}
          renderEmptyState={emptyState ? () => emptyState : undefined}
          data-component="list-box"
        >
          {/* Static children go through the collection too: its dependencies
           * reach the sections' own item lists only from here. */}
          <AriaCollection items={items} dependencies={dependencies}>
            {children as (item: T) => React.ReactNode}
          </AriaCollection>
          {trailing}
          {/* A failed page stops the paging until the row is pressed: the
           * end in view would otherwise ask again at once, and again. */}
          {loadMoreError && !isLoadingMore ? (
            <RetryLoadMoreOption message={loadMoreError} />
          ) : onLoadMore || isLoadingMore ? (
            <AriaListBoxLoadMoreItem
              /* React Aria calls again whenever the collection changes while the
               * end is in view, the loading row's own arrival included. */
              onLoadMore={isLoading || isLoadingMore ? undefined : onLoadMore}
              isLoading={isLoadingMore}
              className={empty()}
            >
              <Spinner size="sm" />
            </AriaListBoxLoadMoreItem>
          ) : null}
        </AriaListBox>
      </Virtualizer>
    </OptionLayoutContext.Provider>
  );
  return isStandalone ? <div className={root()}>{options}</div> : options;
}

interface ListBoxCommonProps {
  /** Values the render function closes over, so a change re-renders the options. */
  dependencies?: unknown[];
  /**
   * `cards` lays each option out as a card, the standalone list of records
   * an entity search shows (picker.md); rows are the list's own look.
   */
  layout?: OptionLayout;
  /** Shown in place of the options when there are none. */
  emptyMessage?: string;
  /**
   * The options are on their way (a search's first answer): an empty list
   * shows a spinner row rather than `emptyMessage`, and `onLoadMore` waits.
   */
  isLoading?: boolean;
  /**
   * Called as the end of the list scrolls near view (within one list height),
   * for a list that pages its results. Not called while `isLoading` or
   * `isLoadingMore`.
   */
  onLoadMore?: () => void;
  /**
   * The next page is on its way: a spinner row after the options. Drop it in
   * the same update that adds the page, or the end still in view asks again.
   */
  isLoadingMore?: boolean;
  /**
   * The next page failed: paging stops and the list ends in a row with this
   * message ("Couldn't load more. Try again."), which asks for the
   * same page again through `onLoadMore` when pressed. Clear it once a page
   * lands or the search changes.
   */
  loadMoreError?: string;
}

interface ListBoxLabelProps {
  /** The list's accessible name: a list on its own has no visible label. */
  'aria-label': string;
  'aria-labelledby'?: never;
}

interface ListBoxLabelledByProps {
  'aria-label'?: never;
  /** The id of the visible text that names the list. */
  'aria-labelledby': string;
}

interface ListBoxSingleProps {
  /** One choice; re-pressing it keeps it. */
  selectionMode: 'single';
  /** The chosen option's id, or null. */
  value: string | null;
  /** Called with the chosen option's id. */
  onChange: (value: string | null) => void;
}

interface ListBoxMultipleProps {
  /** Several choices, toggled one by one. */
  selectionMode: 'multiple';
  /** The chosen options' ids. */
  value: string[];
  /** Called with the chosen ids after a toggle; Escape never empties them. */
  onChange: (value: string[]) => void;
}

export type ListBoxProps<T = object> = ListBoxCommonProps &
  OptionItemsProps<T> &
  (ListBoxLabelProps | ListBoxLabelledByProps) &
  (ListBoxSingleProps | ListBoxMultipleProps);

/**
 * A standalone list of options, always controlled: a list on its own
 * reflects state the caller owns (the MenuSection rule). It is what a cell
 * editor or a filter panel seats inside its own surface; a form picks
 * through Select or Combobox, which render this list inside their popup.
 * The forwarded ref is the list element. No `isDisabled`: React Aria's
 * ListBox has no whole-list disabled state, and a list that cannot be used
 * should not be on screen; disable its options instead.
 */
export const ListBox = forwardRef(function ListBox<T extends object>(
  props: ListBoxProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const listRef = useObjectRef(ref);
  const { suppressed, context } = useListSuppression();

  const selectedKeys: Selection = React.useMemo(
    () =>
      props.selectionMode === 'multiple'
        ? new Set<Key>(props.value)
        : new Set<Key>(props.value == null ? [] : [props.value]),
    [props.selectionMode, props.value],
  );

  /* Suppressed keys never move selection: React Aria selects on press
   * before the option's suppression is consulted, so the bridge drops the
   * toggle here and the controlled props snap the state back. Select-all
   * ('all') has no standalone-list producer worth a contract and is dropped. */
  const onSelectionChange = (keys: Selection) => {
    if (keys === 'all') return;
    if (keys.has(RETRY_LOAD_MORE_KEY)) {
      /* Keyboard focus moves to the last option before the retry row goes:
       * the next page lands right after it, and React Aria never has to
       * find focus a new home mid-render. */
      const list = listRef.current;
      if (list?.contains(document.activeElement)) {
        const options = list.querySelectorAll<HTMLElement>(
          '[role="option"]:not([data-component="retry-load-more"])',
        );
        options[options.length - 1]?.focus();
      }
      props.onLoadMore?.();
      return;
    }
    if (props.selectionMode === 'multiple') {
      const value = withoutSuppressedToggles(
        [...keys].map(String),
        props.value,
        suppressed.current,
      );
      const current = new Set(props.value);
      if (value.length === props.value.length && value.every((id) => current.has(id))) return;
      props.onChange(value);
      return;
    }
    const key = keys.size > 0 ? String([...keys][0]) : null;
    if (key != null && suppressed.current.has(key)) return;
    props.onChange(key);
  };

  const {
    items,
    children,
    dependencies,
    emptyMessage,
    isLoading,
    onLoadMore,
    isLoadingMore,
    loadMoreError,
    layout,
  } = props;
  const labelling =
    props['aria-label'] != null
      ? { 'aria-label': props['aria-label'] }
      : { 'aria-labelledby': props['aria-labelledby'] };

  return (
    <ListSuppressionContext.Provider value={context}>
      <OptionList
        {...labelling}
        items={items}
        listRef={listRef}
        dependencies={dependencies}
        emptyMessage={emptyMessage}
        isLoading={isLoading}
        layout={layout}
        onLoadMore={onLoadMore}
        isLoadingMore={isLoadingMore}
        loadMoreError={loadMoreError}
        selectionMode={props.selectionMode}
        selectedKeys={selectedKeys}
        onSelectionChange={onSelectionChange}
        isStandalone
      >
        {children}
      </OptionList>
    </ListSuppressionContext.Provider>
  );
});
